import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { grey } from "@mui/material/colors";

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpConfigurationService from "@plannel/services/fp/fp-configuration-service";
import PlanScope from "@plannel/pages/factory-plan/common/component/PlanScope";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import { AddButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

// old FpConfiguration.jsx의 setConfigAttributes 대응 — 그룹코드에 따라 상세 그리드의
// defaultValueYn(라디오형 선택)/settingVal(자유 입력값) 컬럼 표시 여부가 달라진다.
const DEFAULT_VALUE_YN_GROUPS = new Set([
  "FP_CUSTOM", "FP_PLAN_DIRECTION", "FP_PRDT_QTY_ACCURACY", "FP_STANDARD_START_DAY",
  "FP_WO_CREATION_GROUP_RULE_POLICY", "FP_WO_CREATION_LOT_SIZE_RULE_POLICY",
]);
const SETTING_VAL_GROUPS = new Set([
  "FP_PLAN_HORIZON", "FP_PLAN_OPTION", "FP_PLAN_SETTING", "FP_PRECISION_ARITHMETIC", "FP_VERSION_NAMING",
]);
const getGroupColumnVisibility = (grpCd) => ({
  defaultValueYnVisible: DEFAULT_VALUE_YN_GROUPS.has(grpCd),
  settingValVisible: SETTING_VAL_GROUPS.has(grpCd),
});

// old styleCallback(FP_PLAN_SETTING 그룹의 Y/N 값, FP_DATE_FORMAT 코드의 날짜서식 목록) 대응.
const FP_DATE_FORMAT_CD = "FP_DATE_FORMAT";
const FP_PLAN_SETTING_GRP = "FP_PLAN_SETTING";
const FP_STOCK_AVAILABILITY_FENCE_TOLERANCE_CD = "FP_STOCK_AVAILABILITY_FENCE_TOLERANCE";
const DATE_FORMAT_VALUES = ["yyyyMMdd", "yyyy-MM-dd", "dd.MM.yyyy", "dd-MM-yyyy", "dd/MM/yyyy"];

const settingValCellEditorSelector = (params) => {
  const { grpCd, comnCd } = params.data ?? {};
  if (comnCd === FP_DATE_FORMAT_CD) {
    return { component: "agRichSelectCellEditor", popup: true, params: { values: DATE_FORMAT_VALUES } };
  }
  if (grpCd === FP_PLAN_SETTING_GRP && comnCd !== FP_STOCK_AVAILABILITY_FENCE_TOLERANCE_CD) {
    return { component: "agRichSelectCellEditor", popup: true, params: { values: ["Y", "N"] } };
  }
  return undefined;
};

// old transactionDtl 대응 — 그리드 행(FpConfigurationDto 모양) → SettingDto(설정값) 매핑.
// isDefYnVisible=true인 그룹은 grpCd별로 settingGrpCd/settingCd가 고정되고(선택형 옵션 그룹),
// 그 외 그룹은 grpCd/comnCd에서 "FP_" 접두어를 뗀 값을 그대로 쓴다(old getSubstring 대응).
const stripFpPrefix = (str) => {
  const idx = str?.indexOf("_") ?? -1;
  return idx !== -1 ? str.substring(idx + 1) : str;
};

const resolveSettingKey = (row, defYnVisible) => {
  const { grpCd, comnCd } = row;

  if (!defYnVisible) {
    return {
      settingGrpCd: grpCd?.startsWith("FP_") ? stripFpPrefix(grpCd) : grpCd,
      settingCd: comnCd?.startsWith("FP_") ? stripFpPrefix(comnCd) : comnCd,
      settingVal: row.settingVal,
    };
  }

  switch (grpCd) {
    case "FP_PLAN_DIRECTION":
      return { settingGrpCd: "PLAN_OPTION", settingCd: "PLAN_DIRECTION", settingVal: stripFpPrefix(comnCd) };
    case "FP_STANDARD_START_DAY":
      return { settingGrpCd: "PLAN_OPTION", settingCd: "STANDARD_START_DAY", settingVal: stripFpPrefix(comnCd) };
    case "FP_WO_CREATION_GROUP_RULE_POLICY":
      return { settingGrpCd: "PLAN_SETTING", settingCd: "WO_CREATION_GROUP_RULE_POLICY", settingVal: stripFpPrefix(comnCd) };
    case "FP_WO_CREATION_LOT_SIZE_RULE_POLICY":
      return { settingGrpCd: "PLAN_SETTING", settingCd: "WO_CREATION_LOT_SIZE_RULE_POLICY", settingVal: stripFpPrefix(comnCd) };
    case "FP_PRDT_QTY_ACCURACY": {
      let settingVal = row.settingVal;
      if (comnCd?.includes("HIGH")) settingVal = "HIGH";
      else if (comnCd?.includes("MEDIUM")) settingVal = "MEDIUM";
      else if (comnCd?.includes("LOW")) settingVal = "LOW";
      return { settingGrpCd: "PRECISION_ARITHMETIC", settingCd: "PRDT_QTY_ACCURACY", settingVal };
    }
    case "FP_CUSTOM":
      return { settingGrpCd: grpCd, settingCd: comnCd, settingVal: row.settingVal };
    default:
      return { settingGrpCd: grpCd, settingCd: comnCd, settingVal: row.settingVal };
  }
};

const buildSettingRow = (row, defYnVisible) => {
  const { settingGrpCd, settingCd, settingVal } = resolveSettingKey(row, defYnVisible);
  return {
    moduleCd: "FP",
    planScope: row.planScope,
    settingGrpCd,
    settingCd,
    settingVal,
    descTxt: row.descrip,
    attr01: row.attr01Val,
    attr02: row.attr02Val,
    attr03: row.attr03Val,
    attr04: row.attr04Val,
    attr05: row.attr05Val,
  };
};

const buildComnCodeRow = (row) => ({
  id: row.id,
  srcId: row.srcId,
  grpCd: row.grpCd,
  comnCd: row.comnCd,
  comnCdNm: row.comnCdNm,
  seq: row.seq,
  descrip: row.descrip,
  defaultValueYn: row.defaultValueYn,
  useYn: row.useYn ?? true,
  attr01Val: row.attr01Val,
  attr02Val: row.attr02Val,
  attr03Val: row.attr03Val,
  attr04Val: row.attr04Val,
  attr05Val: row.attr05Val,
});

// old getSettingChanges 대응 — defaultValueYn이 라디오형 단일 선택인 그룹은, 실제로 바뀐 행이
// 여럿이어도 "현재 defaultValueYn=true인 행" 단 하나만 Setting에 반영한다(그 그룹당 유효한
// 실행 설정은 하나뿐이라는 전제 — comnCodeChanges 쪽은 이 축약 없이 바뀐 행 전부를 그대로 저장).
const getSettingChangeRows = (changedRows, allRows, defYnVisible) => {
  if (!defYnVisible) return changedRows;
  const selected = allRows.find((r) => r.defaultValueYn);
  return selected ? [selected] : changedRows;
};

const audit = {
  // 백엔드 SettingQueryRepository에 z_user 조인이 없어(backend-gaps.md 참고) 사용자명을 못
  // 붙인다 — 우선 일시만 노출한다.
  // TODO(MIG-T2): [master/fpconfiguration] 수정자/생성자 "이름" 표시 미이관.
  //   old: MODIFY_BY/CREATE_BY 컬럼(텍스트, 당시엔 사용자명 문자열이었음).
  //   사유: SettingQueryRepository가 z_user 조인이 없어 Long user id만 내려옴 — backend-gaps.md 등재.
  headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
  children: [
    { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime },
    { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, columnGroupShow: "open" },
  ],
};

// old comnCd/comnCdNm/descrip styleCallback 대응 — 신규 행(id 없음)만 편집 가능, 저장된 행은
// 잠금(§3-3과 동일한 "정의 필드는 생성 시점에만 편집" 규칙을 comnCd 외 comnCdNm/descrip까지 확장).
const definitionEditable = (params) => !params.data?.id;

// old transLangKey(value) 대응 — grpNm/grpDescrip/descrip의 실제 DB 값은 사람이 읽을 수 있는
// 문구인 경우도 있지만("Precision and Rounding"), 다른 다수는 old 자체 번역 키 형태
// ("COMN_GRP_DESCRIP_FP_PLAN_DIRECTION" 등)로 들어있다 — i18n에 그 키가 있으면 번역해서
// 보여주고, 없으면 원본 값을 그대로 보여준다(§8-2와 동일한 폴백 규칙).
const translateOrRaw = (t) => (params) => {
  const value = params.value;
  if (!value) return value;
  const label = t(value);
  return label && label !== value ? label : value;
};

const buildGroupColumnDefs = (t) => [
  { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
  { headerName: "FP_CONFIG_GRP_CD", field: "grpCd", colId: "grpCd", type: ["nonEditableColumn"], width: 220 },
  { headerName: "FP_CONFIG_GRP_NM", field: "grpNm", colId: "grpNm", type: ["nonEditableColumn"], width: 240, valueFormatter: translateOrRaw(t) },
  { headerName: "FP_DESC_TXT", field: "grpDescrip", colId: "grpDescrip", type: ["nonEditableColumn"], width: 320, valueFormatter: translateOrRaw(t) },
];

const buildDetailColumnDefs = (t) => [
  { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
  { headerName: "SRC_ID", field: "srcId", colId: "srcId", hide: true, type: ["nonEditableColumn"] },
  { headerName: "FP_CONFIG_GRP_CD", field: "grpCd", colId: "grpCd", hide: true, type: ["nonEditableColumn"] },
  // old configDetailGridItems: planScope/grpNm은 숨김 필드가 아니라 눈에 보이는 컬럼이다(계획
  // 범위/그룹명을 반복 표시) — Route.js의 "id 식별용 숨김 planScope" 관례와 달리 이 화면은
  // 상세 그리드가 여러 그룹을 오가며 보여주는 조회 성격이 강해 old도 계속 노출해 왔다.
  { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", type: ["nonEditableColumn"], width: 110 },
  { headerName: "FP_CONFIG_GRP_NM", field: "grpNm", colId: "grpNm", type: ["nonEditableColumn"], width: 200, valueFormatter: translateOrRaw(t) },
  {
    // old comnCode 그룹(headerText:"CONFIG", expandable:false, expanded:true) 대응 — 접기/펼치기
    // 토글 없이 항상 두 자식 다 노출(§4의 접기형 그룹과 달리 columnGroupShow를 안 쓴다).
    headerName: "config", groupId: "config", marryChildren: true,
    children: [
      { headerName: "FP_CONFIG_CD", field: "comnCd", colId: "comnCd", cellClass: "stringType", width: 160, editable: definitionEditable },
      { headerName: "FP_CONFIG_NM", field: "comnCdNm", colId: "comnCdNm", cellClass: "stringType", width: 180, editable: definitionEditable },
    ],
  },
  { headerName: "FP_DESC_TXT", field: "descrip", colId: "descrip", cellClass: "stringType", width: 260, editable: definitionEditable, valueFormatter: translateOrRaw(t) },
  { headerName: "FP_DEFAULT_VALUE_YN", field: "defaultValueYn", colId: "defaultValueYn", type: ["booleanColumn", "centerAligned"], width: 120 },
  { headerName: "FP_SETTING_VAL", field: "settingVal", colId: "settingVal", cellClass: "stringType", width: 160, cellEditorSelector: settingValCellEditorSelector },
  {
    headerName: "FP_ATTR", groupId: "attr", marryChildren: true, openByDefault: false,
    children: [
      { headerName: "FP_CONFIG_ATTR_01", field: "attr01Val", colId: "attr01Val", cellClass: "stringType", width: 130 },
      { headerName: "FP_CONFIG_ATTR_02", field: "attr02Val", colId: "attr02Val", cellClass: "stringType", width: 130, columnGroupShow: "open" },
      { headerName: "FP_CONFIG_ATTR_03", field: "attr03Val", colId: "attr03Val", cellClass: "stringType", width: 130, columnGroupShow: "open" },
      { headerName: "FP_CONFIG_ATTR_04", field: "attr04Val", colId: "attr04Val", cellClass: "stringType", width: 130, columnGroupShow: "open" },
      { headerName: "FP_CONFIG_ATTR_05", field: "attr05Val", colId: "attr05Val", cellClass: "stringType", width: 130, columnGroupShow: "open" },
    ],
  },
  audit,
];

/**
 * FP 환경설정(FpConfiguration) — old FpConfiguration.jsx 대응. Master 표준 CRUD 화면과 달리
 * 마스터-디테일 2단 그리드(상단: 설정 그룹 목록, 읽기전용 / 하단: 선택된 그룹의 설정값,
 * 편집 가능)로 구성된다. 상단 그리드에서 그룹을 클릭하면 하단 그리드를 그 그룹 기준으로 다시
 * 조회한다(master-detail, bor/BorSetTab.js의 상단 grid onRowClicked → 하단 grid 재조회 패턴과
 * 동일). defaultValueYn은 그룹 내 라디오형 단일 선택(BorSetTab.js의 isMainResource와 동일한
 * onCellValueChanged 패턴)이며, 그룹에 따라 defaultValueYn/settingVal 컬럼 표시 여부와
 * settingVal의 편집기(자유 입력/Y-N/날짜서식 목록)가 달라진다. 행 추가/삭제는 FP_CUSTOM
 * 그룹에서만 허용된다(사용자 정의 설정 그룹).
 */
const FpConfiguration = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [planScope, setPlanScope] = useState(reduxViewState?.planScope ?? "");
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [details, setDetails] = useState([]);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const groupGridRef = useRef();
  const detailGridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const latestGroupReqRef = useRef(0);
  const latestDetailReqRef = useRef(0);

  const groupGridMemo = useMemo(() => ({
    ...FpGridSetting({ title, viewName: `${viewName}_GRP` }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);
  const detailGridMemo = useMemo(() => ({
    ...FpGridSetting({ title, viewName: `${viewName}_DTL` }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // grpNm/grpDescrip/descrip의 translateOrRaw 포맷터가 t()를 closure로 잡아야 해서(§8-2와 동일
  // 이유) columnDefs는 모듈 상수가 아니라 컴포넌트 안에서 t/language에 의존해 만든다.
  const groupColumnDefs = useMemo(() => buildGroupColumnDefs(t), [t]);
  const detailColumnDefs = useMemo(() => buildDetailColumnDefs(t), [t]);

  const retrieveGroups = () => {
    if (!planScope) return;
    const reqId = ++latestGroupReqRef.current;
    groupGridRef.current?.api?.setGridOption("loading", true);

    fpConfigurationService.getGroups({ planScope })
      .then((response) => {
        if (reqId !== latestGroupReqRef.current) return;
        const results = response.data;
        setGroups(isEmpty(results) ? [] : results);
        setSelectedGroup(null);
        setDetails([]);
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch((e) => debug(e))
      .then(() => {
        if (reqId !== latestGroupReqRef.current) return;
        const reduxState = reduxUtil.createObjectState(viewName, { planScope });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        groupGridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const retrieveDetails = (grpCd) => {
    if (!planScope || !grpCd) return;
    const reqId = ++latestDetailReqRef.current;
    detailGridRef.current?.api?.setGridOption("loading", true);

    fpConfigurationService.getDetails({ planScope, groupCd: grpCd })
      .then((response) => {
        if (reqId !== latestDetailReqRef.current) return;
        setDetails(isEmpty(response.data) ? [] : response.data);
      })
      .catch((e) => debug(e))
      .then(() => {
        if (reqId !== latestDetailReqRef.current) return;
        DataState.initialize(detailGridRef.current?.api);
        detailGridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGroupGridReady = (params) => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs: groupColumnDefs, viewName: `${viewName}_GRP`, initState: true });
  };

  const onDetailGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: detailColumnDefs, viewName: `${viewName}_DTL`, initState: true });
  };

  const onGroupRowClicked = (e) => {
    if (!e.data) return;
    const { id, grpCd, grpNm } = e.data;
    setSelectedGroup({ id, grpCd, grpNm });
    retrieveDetails(grpCd);
  };

  // defaultValueYn은 old onCellClicked의 라디오형 단일 선택 대응 — true로 바뀐 행 외 나머지는
  // 자동으로 false(bor/BorSetTab.js의 isMainResource와 동일 패턴).
  const onDetailCellValueChanged = (e) => {
    DataState.setDataState(e);
    if (e.colDef.colId !== "defaultValueYn" || !e.newValue) return;
    e.api.forEachNode((node) => {
      if (node.id !== e.node.id && node.data?.defaultValueYn) {
        node.setDataValue("defaultValueYn", false);
      }
    });
  };

  // 그룹에 따라 defaultValueYn/settingVal 컬럼 표시 여부가 달라진다(old setConfigAttributes) —
  // columnDefs를 통째로 다시 세팅하지 않고 컬럼 표시만 토글한다.
  useEffect(() => {
    const api = detailGridRef.current?.api;
    if (!api) return;
    const { defaultValueYnVisible, settingValVisible } = getGroupColumnVisibility(selectedGroup?.grpCd);
    api.setColumnsVisible(["defaultValueYn"], defaultValueYnVisible);
    api.setColumnsVisible(["settingVal"], settingValVisible);
  }, [selectedGroup]);

  // old setVersionSample 대응 — FP_VERSION_NAMING 그룹 선택 시, 저장된 설정값으로 버전 문자열
  // 예시를 실시간 계산해 보여준다(그리드에 이미 로드된 값 기준, 미저장 편집까지 즉시 반영하진 않음).
  const versionSample = useMemo(() => {
    if (selectedGroup?.grpCd !== "FP_VERSION_NAMING") return "";
    const findVal = (comnCd) => details.find((d) => d.comnCd === comnCd)?.settingVal || "";
    const delimiter = findVal("FP_DELIMITER");
    const dateFormat = findVal("FP_DATE_FORMAT") || "yyyyMMdd";
    const prefix = findVal("FP_PREFIX");
    const suffix = findVal("FP_SUFFIX");
    const seqFormat = Number(findVal("FP_SEQ_FORMAT")) || 1;

    let version = `${t("FP_VERSION_SAMPLE")}: `;
    if (prefix) version += `${prefix}${delimiter}`;
    version += dateUtils.formatDate(new Date(), dateFormat);
    version += delimiter;
    if (suffix) version += `${suffix}${delimiter}`;
    version += "1".padStart(seqFormat, "0");
    return version;
  }, [selectedGroup, details, t]);

  const insertRow = () => {
    const newRow = {
      id: null,
      planScope,
      srcId: selectedGroup?.id,
      grpCd: selectedGroup?.grpCd,
      grpNm: selectedGroup?.grpNm,
      comnCd: "",
      comnCdNm: "",
      descrip: "",
      defaultValueYn: false,
      useYn: true,
      settingVal: "",
      attr01Val: "", attr02Val: "", attr03Val: "", attr04Val: "", attr05Val: "",
    };

    DataState.setDataState(GridUtils.addRow(detailGridRef.current.api, newRow));
  };

  const saveDetail = () => {
    detailGridRef.current.api.stopEditing();
    const changedRows = DataState.getAllStateData(detailGridRef.current.api);

    if (changedRows.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    const allRows = GridUtils.getAllData(detailGridRef.current.api);
    const { defaultValueYnVisible } = getGroupColumnVisibility(selectedGroup?.grpCd);
    const settingChanges = getSettingChangeRows(changedRows, allRows, defaultValueYnVisible)
      .map((row) => buildSettingRow(row, defaultValueYnVisible));
    const comnCodeChanges = changedRows.map(buildComnCodeRow);

    fpConfigurationService.save(settingChanges, comnCodeChanges)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
      .catch((e) => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => retrieveDetails(selectedGroup?.grpCd));
  };

  const removeDetail = () => {
    const selectedRows = detailGridRef.current.api.getSelectedRows();

    if (selectedRows.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 미저장 신규 행(id 없음)은 서버에 존재한 적이 없으니 삭제 API 대상이 아니다 — 재조회만으로
    // 그리드에서 사라진다(old onDelete도 rowState==='created' 행은 삭제 API에서 제외).
    const savedRows = selectedRows.filter((row) => row.id);
    const finish = () => {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
      retrieveDetails(selectedGroup?.grpCd);
    };

    if (savedRows.length === 0) {
      finish();
      return;
    }

    const { defaultValueYnVisible } = getGroupColumnVisibility(selectedGroup?.grpCd);
    const settingChanges = savedRows.map((row) => buildSettingRow(row, defaultValueYnVisible));
    const comnCodeChanges = savedRows.map(buildComnCodeRow);

    fpConfigurationService.remove(settingChanges, comnCodeChanges)
      .then(finish)
      .catch(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") }));
  };

  const handleDialog = (action) => {
    if (action === "SAVE") saveDetail();
    else if (action === "DELETE") removeDetail();
  };

  // planScope 변경 시에만 그룹 목록 재조회(§6) — 상세 그리드는 그룹 행 클릭(master-detail)으로만 갱신.
  useEffect(() => {
    if (!gridLoading) return;
    retrieveGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridLoading, planScope]);

  const isCustomGroup = selectedGroup?.grpCd === "FP_CUSTOM";

  return (
    <>
      <FilterContainer>
        <PlanScope value={planScope} onChange={setPlanScope} />
        <Box />
      </FilterContainer>
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 0.4, mb: 1, minHeight: 0 }}>
          {/* old ButtonArea title={transLangKey("GRP_CONF")} 대응 — 그룹 그리드 위 고정 섹션 제목. */}
          <Box sx={{ mb: 1, minHeight: 40, display: "flex", alignItems: "center", fontWeight: 500 }}>{t("group")}</Box>
          <Box id="fp-configuration-group-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, pb: 1 }}>
            <AgGridReact
              ref={groupGridRef}
              rowData={groups}
              {...groupGridMemo}
              onRowClicked={onGroupRowClicked}
              onGridReady={onGroupGridReady}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 0.6, minHeight: 0 }}>
          {/* old ButtonArea title={transLangKey("UI_DP_01")} 대응 — 상세 그리드 위 고정 섹션
              제목(선택된 그룹명이 아니라 항상 같은 "환경 설정" 문구). 버전 예시는 old
              LeftButtonArea처럼 제목 옆에 별도로 붙인다(FP_VERSION_NAMING 그룹일 때만). */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, minHeight: 40 }}>
            {/* 섹션 제목(굵기 500) + 부가정보(구분선 뒤 보조색) — FP 그리드 섹션 라벨 공통 형태
                (fp-master-screen-guide.md §15-1, 기준 화면 master/Bom.js). */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <Box component="span" sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>{t("menuFpConfiguration")}</Box>
              {versionSample && (
                <>
                  <Divider orientation="vertical" sx={{ height: 16, alignSelf: "center", borderColor: grey[400] }} />
                  <Box
                    component="span"
                    sx={{ color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {versionSample}
                  </Box>
                </>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isCustomGroup && <AddButton onClick={insertRow} />}
              {isCustomGroup && <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />}
              <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
            </Box>
          </Box>
          <Box id="fp-configuration-detail-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, pb: 1 }}>
            <AgGridReact
              ref={detailGridRef}
              rowData={details}
              {...detailGridMemo}
              rowSelection={{ ...detailGridMemo.rowSelection, headerCheckbox: true, checkboxes: true }}
              onCellValueChanged={onDetailCellValueChanged}
              onGridReady={onDetailGridReady}
            />
          </Box>
        </Box>
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(FpConfiguration);
